import { withAssign } from '@reatom/framework'
import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { PnlChart } from '@root/features/portfolio/user-pnl-chart'
import { PnlOwner } from '@root/features/portfolio/user-pnl-chart/model/pnl-relation'
import { PnlCalendar } from '@root/features/portfolio/user-pnl-chart/ui/pnl-calendar/pnl-calendar'
import { UserPnlSummary } from '@root/features/portfolio/user-pnl-chart/ui/user-pnl/user-pnl-summary'
import { ProfileOwner } from '@root/features/user/profile/model/profile-owner'
import { useTranslation } from '@root/shared/localization'
import { Calendar2Icon } from '@root/shared/ui-kit'

import { USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP } from '../../models/user-trading-analytics-period'
import { UserTradingAnalyticsPnlView } from '../../models/user-trading-analytics-pnl-view'

import type { UserTradingAnalyticsModel } from '../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: UserTradingAnalyticsModel
  owner: ProfileOwner
} & ClassNameProps

const ProfileOwnerToPnlOwnerMap: Record<ProfileOwner, PnlOwner> = {
  [ProfileOwner.CurrentUser]: PnlOwner.CurrentUser,
  [ProfileOwner.AnotherUsers]: PnlOwner.AnotherUsers,
}

export const UserProfilePnl: React.FC<Props> = ({
  className,
  model,
  owner,
}) => {
  const { t } = useTranslation()
  const [view, setPnlView] = useAtom(model.pnlViewAtom)
  const [trend] = useAtom(model.trendAtom)
  const [priceType] = useAtom(model.priceTypeAtom)
  const [requestState] = useAtom(model.requestStateAtom)

  const handleViewToggle = () =>
    setPnlView(
      view === UserTradingAnalyticsPnlView.CALENDAR
        ? UserTradingAnalyticsPnlView.CHART
        : UserTradingAnalyticsPnlView.CALENDAR,
    )

  const [period] = useAtom(model.periodAtom)

  return (
    <Root className={className} data-trend={trend}>
      <Outline>
        <UserPnlSummary
          period={USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP[period]}
          priceType={priceType}
          requestState={requestState}
        />
        {view === UserTradingAnalyticsPnlView.CALENDAR ? (
          <PnlCalendar
            onToggleView={handleViewToggle}
            model={model.calendarModel}
            priceType={priceType}
          />
        ) : (
          <ChartWrapper>
            <ViewToggle>
              <ViewToggleButton type="button" onClick={handleViewToggle}>
                <Calendar2Icon size={16} />
                {t('portfolio.pnl-chart.view-calendar')}
              </ViewToggleButton>
            </ViewToggle>
            <UserPnlChart
              owner={ProfileOwnerToPnlOwnerMap[owner]}
              model={model.pnlChartModelAtom.pipe(
                withAssign(() => ({
                  priceType: model.priceTypeAtom,
                })),
              )}
              trend={trend}
            />
          </ChartWrapper>
        )}
      </Outline>
      <Gradient />
    </Root>
  )
}

const Root = styled.div`
  isolation: isolate;
  position: relative;
  box-sizing: border-box;
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: grid;
  min-height: 0;

  &[data-trend='up'] {
    --pnl-color: var(--up-9);
  }
  &[data-trend='down'] {
    --pnl-color: var(--down-9);
  }
  &[data-trend='neutral'] {
    --pnl-color: var(--neutral-9);
  }
`

const ChartWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 20px;
  height: 100%;
  min-height: 0;

  padding-block-end: 16px;
`

const Outline = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  min-height: 0;

  background:
    radial-gradient(
      68% 62.18% at 29.1% -11.85%,
      color-mix(in srgb, var(--pnl-color) 20%, transparent) 27.7%,
      color-mix(in srgb, var(--pnl-color) 16%, transparent) 47.27%,
      color-mix(in srgb, var(--pnl-color) 4%, transparent) 71.15%,
      transparent 88.57%
    ),
    radial-gradient(
      51.72% 50.4% at 36.53% 103.08%,
      color-mix(in srgb, var(--pnl-color) 20%, transparent) 4.78%,
      color-mix(in srgb, var(--pnl-color) 16%, transparent) 42.79%,
      color-mix(in srgb, var(--pnl-color) 6%, transparent) 68.27%,
      transparent 100%
    ),
    radial-gradient(
      107.31% 377.35% at 135.68% -217.8%,
      color-mix(in srgb, var(--pnl-color) 7%, transparent) 33%,
      color-mix(in srgb, var(--pnl-color) 1%, transparent) 100%
    );
  backdrop-filter: blur(1.5px);
`

const ViewToggle = styled.div`
  display: grid;
  justify-content: start;
  margin-top: 16px;
  margin-bottom: 4px;
`

const ViewToggleButton = styled.button`
  all: unset;
  cursor: pointer;
  color: var(--textA-9);

  display: grid;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;

  grid-template-columns: auto auto;
  gap: 6px;
  align-content: center;
  align-items: center;
  padding-inline: 16px;
`

const UserPnlChart = styled(PnlChart)`
  padding-inline: 7px 7px;
  height: 100%;
  min-height: 0;
`

const Gradient = () => (
  <svg
    viewBox="-0.3 -0.3 646.6 308.6"
    preserveAspectRatio="none"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: 'absolute',
      inset: 0,
      zIndex: 1,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
    }}
  >
    <g data-figma-bg-blur-radius="3">
      <rect
        width="646"
        height="308"
        rx="10"
        fill="url(#paint0_radial_212_294)"
        fillOpacity="0.99"
      />
      <rect
        x="0.3"
        y="0.3"
        width="646"
        height="308"
        rx="9.7"
        stroke="url(#paint1_radial_212_294)"
        strokeOpacity="0.65"
        strokeWidth="1"
      />
      <rect
        x="0.3"
        y="0.3"
        width="646"
        height="308"
        rx="9.7"
        stroke="url(#paint2_radial_212_294)"
        strokeOpacity="0.25"
        strokeWidth="1"
      />
    </g>
    <defs>
      <clipPath id="bgblur_0_212_294_clip_path" transform="translate(3 3)">
        <rect width="646" height="308" rx="10" />
      </clipPath>
      <radialGradient
        id="paint0_radial_212_294"
        cx="0"
        cy="0"
        r="1"
        gradientTransform="matrix(-672.427 1117.15 -168.521 -320.6 876.505 -670.814)"
        gradientUnits="userSpaceOnUse"
      >
        <stop
          offset="0.330013"
          stopColor="var(--pnl-color)"
          stopOpacity="0.07"
        />
        <stop offset="1" stopColor="var(--pnl-color)" stopOpacity="0.01" />
      </radialGradient>
      <radialGradient
        id="paint1_radial_212_294"
        cx="0"
        cy="0"
        r="1"
        gradientTransform="matrix(-776.829 1301.56 -180.753 -339.628 741.432 -495.932)"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="var(--pnl-color)" />
        <stop offset="1" stopColor="var(--pnl-color)" stopOpacity="0" />
      </radialGradient>
      <radialGradient
        id="paint2_radial_212_294"
        cx="0"
        cy="0"
        r="1"
        gradientTransform="matrix(180.586 401.966 -60.6359 86.1 -19.0864 -54.8136)"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="var(--pnl-color)" />
        <stop offset="1" stopColor="var(--pnl-color)" stopOpacity="0" />
      </radialGradient>
    </defs>
  </svg>
)
